import { InViewport } from "@gik/core/components/InViewport";
import { ErrorPageSection } from "@gik/core/pages/Error/ErrorPageSection";
import bemBlock from "@gik/core/utils/bemBlock";
import { getAssetsUrl } from "@gik/core/utils/LinkUtils";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import i18n from "@gik/i18n";
import { CompetitorsPreviewSection } from "@gik/marketing/components/CompetitorsPreview";
import { FeaturesNav } from "@gik/marketing/components/FeaturesNav";
import { MarketingNavSection } from "@gik/marketing/components/MarketingNav";
import type { AccordionItemType } from "@gik/ui-legacy/Accordion";
import { Accordion } from "@gik/ui-legacy/Accordion";
import { PageHeader } from "@gik/ui-legacy/gik/PageHeader";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { StartInkindCard } from "@gik/ui-legacy/gik/StartInkindCard";
import { TestimonialsCarouselSection } from "@gik/ui-legacy/gik/TestimonialsCarouselSection";
import { HTMLParser } from "@gik/ui-legacy/HTMLParser";
import { Image } from "@gik/ui-legacy/Image";
import { Sticky } from "@gik/ui-legacy/Sticky";
import React from "react";
import { useTranslation } from "react-i18next";
import { FeatureContent } from "../../components/FeatureContent";
import { translationKeys } from "./i18n/en";

export const FeaturesPageBlockName = "features-page";

function createAccordionItems(
	prefix: string,
	totalItems: number,
): AccordionItemType[] {
	return [...Array(totalItems)].map((_, index) => ({
		title: HTMLParser({
			rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Title`]),
		}),
		content: HTMLParser({
			rawHtml: i18n.t(translationKeys[`${prefix}${index + 1}Content`]),
		}),
	}));
}

function FeaturesPageComp(): React.ReactElement {
	const bem = bemBlock(FeaturesPageBlockName);
	const { t } = useTranslation();

	const calendarFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("calendarFAQ", 3),
		[],
	);
	const wishlistFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("wishlistFAQ", 3),
		[],
	);
	const updatesFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("updatesFAQ", 2),
		[],
	);
	const fundraisingFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("fundraisingFAQ", 3),
		[],
	);

	// prettier-ignore
	const moreFAQItems: AccordionItemType[] = React.useMemo(
		() => createAccordionItems("moreFAQ", 3),
		[],
	);

	const calendarIcon = (
		<Image
			style={{ height: "35px" }}
			src={[
				`${getAssetsUrl()}marketing/icons/calendar.png`,
				`${getAssetsUrl()}marketing/icons/calendar@2x.png 2x`,
			]}
		/>
	);
	const wishlistIcon = (
		<Image
			style={{ height: "35px" }}
			src={[
				`${getAssetsUrl()}marketing/icons/wishlist.png`,
				`${getAssetsUrl()}marketing/icons/wishlist@2x.png 2x`,
			]}
		/>
	);
	const updatesIcon = (
		<Image
			style={{ height: "35px" }}
			src={[
				`${getAssetsUrl()}marketing/icons/updates.png`,
				`${getAssetsUrl()}marketing/icons/updates@2x.png 2x`,
			]}
		/>
	);
	const fundraisingIcon = (
		<Image
			style={{ height: "35px" }}
			src={[
				`${getAssetsUrl()}marketing/icons/fundraising.png`,
				`${getAssetsUrl()}marketing/icons/fundraising@2x.png 2x`,
			]}
		/>
	);
	const moreIcon = (
		<Image
			style={{ height: "35px" }}
			src={[
				`${getAssetsUrl()}marketing/icons/sparkle.png`,
				`${getAssetsUrl()}marketing/icons/sparkle@2x.png 2x`,
			]}
		/>
	);

	return (
		<div className={bem()}>
			<InViewport height={176}>
				<PageHeader
					variant="love"
					decoration="foliage"
					className="gik-page-section--with-nav"
				>
					<h1>{t(translationKeys.title)}</h1>
				</PageHeader>
			</InViewport>
			<Sticky className={bem("sticky-nav")}>
				<InViewport height={60}>
					<FeaturesNav />
				</InViewport>
			</Sticky>

			<InViewport height={167}>
				<PageSection noPadBottom noPadTop className={bem("lead")}>
					<p className="gik-section-lead">
						{t(translationKeys.leadDescription)}
					</p>
				</PageSection>
			</InViewport>

			<InViewport height={523}>
				<PageSection id="calendar">
					<FeatureContent
						title={
							<div className={bem("feature-title")}>
								{calendarIcon}
								<span>{t(translationKeys.calendarTitle)}</span>
							</div>
						}
						description={<p>{t(translationKeys.calendarDescription)}</p>}
						aside={
							<Image
								alt="Calendar example"
								className={bem("calendar-example")}
								src={[
									`${getAssetsUrl()}marketing/featuresPage/calendar1x.png`,
									`${getAssetsUrl()}marketing/featuresPage/calendar2x.png 2x`,
								]}
							/>
						}
						headingLevel={2}
					>
						<Accordion headingLevel={3} items={calendarFAQItems} dividers />
					</FeatureContent>
				</PageSection>
			</InViewport>

			<InViewport height={584}>
				<PageSection id="wishlist">
					<FeatureContent
						title={
							<div className={bem("feature-title")}>
								{wishlistIcon}
								<span>{t(translationKeys.wishlistTitle)}</span>
							</div>
						}
						description={<p>{t(translationKeys.wishlistDescription)}</p>}
						aside={
							<Image
								alt="Wishlist example"
								className={bem("wishlist-example")}
								src={[
									`${getAssetsUrl()}marketing/featuresPage/wishlist1x.png`,
									`${getAssetsUrl()}marketing/featuresPage/wishlist2x.png 2x`,
								]}
							/>
						}
						variant="danger"
						headingLevel={2}
						reversed
					>
						<Accordion headingLevel={3} items={wishlistFAQItems} dividers />
					</FeatureContent>
				</PageSection>
			</InViewport>

			<InViewport height={489}>
				<PageSection id="updates">
					<FeatureContent
						title={
							<div className={bem("feature-title")}>
								{updatesIcon}
								<span>{t(translationKeys.updatesTitle)}</span>
							</div>
						}
						description={<p>{t(translationKeys.updatesDescription)}</p>}
						aside={
							<Image
								alt="Updates example"
								className={bem("updates-example")}
								src={[
									`${getAssetsUrl()}marketing/featuresPage/updates1x.png`,
									`${getAssetsUrl()}marketing/featuresPage/updates2x.png 2x`,
								]}
							/>
						}
						variant="secondary"
						headingLevel={2}
					>
						<Accordion headingLevel={3} items={updatesFAQItems} dividers />
					</FeatureContent>
				</PageSection>
			</InViewport>

			<InViewport height={451}>
				<PageSection id="donations">
					<FeatureContent
						title={
							<div className={bem("feature-title")}>
								{fundraisingIcon}
								<span className="tw-text-success-600">
									{t(translationKeys.fundraisingTitle)}
								</span>
							</div>
						}
						description={<p>{t(translationKeys.fundraisingDescription)}</p>}
						aside={
							<Image
								alt="Donations example"
								className={bem("fundraising-example")}
								src={[
									`${getAssetsUrl()}marketing/featuresPage/fundraising1x.png`,
									`${getAssetsUrl()}marketing/featuresPage/fundraising2x.png 2x`,
								]}
							/>
						}
						variant="success"
						headingLevel={2}
						reversed
					>
						<Accordion headingLevel={3} items={fundraisingFAQItems} dividers />
					</FeatureContent>
				</PageSection>
			</InViewport>

			<InViewport height={594}>
				<PageSection id="more">
					<FeatureContent
						title={
							<div className={bem("feature-title")}>
								{moreIcon}
								<span className="tw-text-warn-700">
									{t(translationKeys.moreTitle)}
								</span>
							</div>
						}
						description={<p>{t(translationKeys.moreDescription)}</p>}
						aside={
							<Image
								alt="More example"
								className={bem("more-example")}
								src={[
									`${getAssetsUrl()}marketing/featuresPage/more1x.png`,
									`${getAssetsUrl()}marketing/featuresPage/more2x.png 2x`,
								]}
							/>
						}
						variant="warn"
						headingLevel={2}
					>
						<Accordion headingLevel={3} items={moreFAQItems} dividers />
					</FeatureContent>
				</PageSection>
			</InViewport>
			<br />

			<InViewport height={831}>
				<CompetitorsPreviewSection />
			</InViewport>

			<InViewport height={175}>
				<StartInkindCard trackingId={CreateClickedLocation.PAGE_FEATURES} />
			</InViewport>

			<InViewport height={330}>
				<TestimonialsCarouselSection />
			</InViewport>

			<InViewport height={461}>
				<MarketingNavSection variant="love" />
			</InViewport>
		</div>
	);
}

export const FeaturesPage = withComponentErrorBoundary(FeaturesPageComp, {
	ErrorFallback: ErrorPageSection,
});
